import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, Facebook, Clock, MapPin } from "lucide-react";
import { BRAND } from "../lib/constants";
import QuoteForm from "../components/QuoteForm";
import ConsultationForm from "../components/ConsultationForm";
import ContactForm from "../components/ContactForm";
import HeroShield from "../components/HeroShield";

const tabs = [
  { id: "quote", label: "Request a Quote" },
  { id: "consult", label: "Book a Consultation" },
  { id: "message", label: "Send a Message" },
];

const Contact = () => {
  const location = useLocation();
  const [active, setActive] = useState("quote");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const form = params.get("form");
    if (form && tabs.some((t) => t.id === form)) {
      setActive(form);
    } else if (form === "message") {
      setActive("message");
    }
  }, [location.search]);

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--isa-navy)] text-white">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${BRAND.images.statsBg}')` }}
        />
        <div className="absolute inset-0 bg-[color:var(--isa-navy)]/85" />
        <HeroShield position="right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <span className="eyebrow text-[color:var(--isa-gold)]">Contact</span>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Real people, real <em className="not-italic text-[color:var(--isa-gold)]">guidance</em>.
          </h1>
          <p className="mt-7 text-base lg:text-lg font-sans text-white/80 max-w-3xl leading-relaxed">
            Request a free quote, book a no-pressure consultation, or simply send us a message.
            We'll respond within one business day.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — Contact info */}
          <div className="lg:col-span-4 space-y-8" data-testid="contact-info">
            <div>
              <span className="eyebrow">Reach Us</span>
              <h2 className="mt-4 section-heading text-3xl lg:text-4xl">
                Multiple ways to <em className="not-italic text-[color:var(--isa-gold)]">connect</em>.
              </h2>
              <div className="gold-divider" />
              <p className="text-sm font-sans text-gray-600 leading-relaxed">
                Prefer to chat live? Give us a call. Want it in writing? Send a message. Need a
                strategy session? Schedule a consultation.
              </p>
            </div>

            <ul className="space-y-6">
              <li className="flex items-start gap-4 p-5 border border-gray-200">
                <Phone className="w-5 h-5 text-[color:var(--isa-gold)] mt-1" strokeWidth={1.6} />
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.18em] text-gray-500">Phone</p>
                  <a
                    href={`tel:${BRAND.phoneTel}`}
                    className="block mt-1 font-serif text-xl text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    {BRAND.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4 p-5 border border-gray-200">
                <Mail className="w-5 h-5 text-[color:var(--isa-gold)] mt-1" strokeWidth={1.6} />
                <div className="min-w-0">
                  <p className="text-xs font-sans uppercase tracking-[0.18em] text-gray-500">Email</p>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="block mt-1 font-serif text-lg text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors break-all"
                    data-testid="contact-email-link"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4 p-5 border border-gray-200">
                <MapPin className="w-5 h-5 text-[color:var(--isa-gold)] mt-1" strokeWidth={1.6} />
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.18em] text-gray-500">Office</p>
                  <a
                    href={BRAND.addressMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-1 font-serif text-lg text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
                    data-testid="contact-address-link"
                  >
                    {BRAND.address}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4 p-5 border border-gray-200">
                <Clock className="w-5 h-5 text-[color:var(--isa-gold)] mt-1" strokeWidth={1.6} />
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.18em] text-gray-500">Response Time</p>
                  <p className="mt-1 font-sans text-sm text-[color:var(--isa-navy)]">
                    Within one business day
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4 p-5 border border-gray-200">
                <Facebook className="w-5 h-5 text-[color:var(--isa-gold)] mt-1" strokeWidth={1.6} />
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.18em] text-gray-500">Follow Us</p>
                  <a
                    href={BRAND.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block font-sans text-sm text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
                    data-testid="contact-facebook-link"
                  >
                    Facebook
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* RIGHT — Tabs + Forms */}
          <div className="lg:col-span-8" data-testid="contact-forms">
            <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 mb-8">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`relative px-5 py-3 font-sans text-sm font-semibold tracking-wide transition-colors ${
                    active === t.id
                      ? "text-[color:var(--isa-navy)]"
                      : "text-gray-500 hover:text-[color:var(--isa-navy)]"
                  }`}
                >
                  {t.label}
                  {active === t.id && (
                    <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[color:var(--isa-gold)]" />
                  )}
                </button>
              ))}
            </div>

            {active === "quote" && <QuoteForm />}
            {active === "consult" && <ConsultationForm />}
            {active === "message" && <ContactForm />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
